import React, { useCallback } from "react";
import {
  Columns2,
  //DownloadIcon, // Hide for now
  ListIcon,
  //MailIcon, // Hide for now
  ShareIcon,
} from "lucide-react";
import {
  Button,
  Tabs,
  TabsList,
  TabsTrigger,
  TabsContent,
  Card,
  CardContent,
  CardHeader,
} from "@/components/ui";
import { StudentGrid } from "@/components/StudentGrid";
import { Student } from "@/types/Student";
import { useTranslation } from "react-i18next";
import { HeaderActions, ActionItem } from "./HeaderActions";
import { StudentsListSGrid } from "./Grid/StudentsListSGrid";
import { StudentOrdering } from "@/types/StudentOrdering";
import { useOrg } from "@/hooks/useOrg";
import { RiseStar } from "@/assets/svg/RiseStar";

export enum View {
  GRID = "grid",
  TABLE = "table",
}

export const NUMS_GRID_ROWS = 3;

interface StudentListWrapperProps {
  title: string;
  description: string;
  onPerPageChange?: (size: number) => void;
  onPageChange: (page: number) => void;
  onShareClick?: () => void;
  page: number;
  perPage: number;
  onViewChange?: (view: View) => void;
  view: View;
  students: Student[];
  totalCount: number;
  loading: boolean;
  enableGridView?: boolean;
  showMessages?: boolean;
  ordering?: StudentOrdering;
  setOrdering?: (ordering: StudentOrdering) => void;
  isError?: boolean;
  hasActiveFilters?: boolean;
  riseIcon?: boolean;
  profileSource?: string;
}

export const StudentListWrapper = ({
  title,
  description,
  students,
  totalCount,
  onPageChange,
  onPerPageChange,
  onShareClick,
  perPage,
  page,
  onViewChange,
  view,
  loading,
  enableGridView = true,
  showMessages = false,
  ordering,
  setOrdering,
  isError = false,
  hasActiveFilters = false,
  riseIcon,
  profileSource,
}: StudentListWrapperProps) => {
  const { org } = useOrg();
  const { t } = useTranslation();

  const totalPages = Math.ceil(totalCount / perPage);

  /* Hide for now
  const handleExportToCSV = () => {
    // Implementation for CSV export would go here

    // TODO: Replace with toast notification once implemented
    // Toast success message from i18n
    alert(t("studentList.toast.exportSuccess"));
  };
  */

  /* Hide for now
  const handleSendBulkMessage = () => {
    // Implementation for opening the Create Bulk Message flow would go here

    // TODO: Replace with toast notification once implemented
    // Toast success message from i18n
    alert(t("studentList.toast.bulkMessageSuccess"));
  };
  */

  const headerActions: ActionItem[] = [
    /* Hide for now until we have support for these functions.
    {
      key: "export-csv",
      render: () => (
        <>
          <DownloadIcon className="mr-2 h-4 w-4" />
          {t("studentList.actions.exportCsv")}
        </>
      ),
      onClick: handleExportToCSV,
    },
    {
      key: "bulk-message",
      render: () => (
        <>
          <MailIcon className="mr-2 h-4 w-4" />
          {t("studentList.actions.sendBulkMessage")}
        </>
      ),
      onClick: handleSendBulkMessage,
    },
    */
  ];

  const setPerPage = useCallback(
    (newPageSize: number) => {
      if (!enableGridView) return;
      if (view === View.GRID && onPerPageChange) {
        onPerPageChange(newPageSize);
      }
    },
    [enableGridView, view, onPerPageChange],
  );

  return (
    <Tabs
      value={enableGridView ? view : "table"}
      onValueChange={(val: string) => onViewChange && onViewChange(val as View)}
      id="view-tabs"
    >
      <Card>
        <CardHeader>
          <div className="flex items-center justify-between">
            <div className="flex">
              <section
                className="p-0"
                data-testid="table-title"
                aria-labelledby="table-title-label"
                role="region"
              >
                <div className="mb-1 flex gap-2">
                  {riseIcon && <RiseStar />}
                  <h2
                    data-testid="table-title-h1"
                    id="table-title-label"
                    className="text-[16px] leading-tight font-semibold"
                    tabIndex={-1}
                  >
                    {title}
                  </h2>
                </div>
                <p
                  className="text-sm text-primary-light"
                  id="table-description"
                  data-testid="table-description"
                  aria-label={description}
                >
                  {description}
                </p>
              </section>
            </div>
            <div className="flex h-8 items-center gap-4">
              {enableGridView && (
                <TabsList
                  className="h-8 rounded-sm p-1"
                  aria-label={t("global.viewOptions")}
                >
                  <TabsTrigger
                    value="grid"
                    id="grid-view-tab"
                    className="w-15 px-3"
                  >
                    <span className="sr-only">{t("studentList.gridView")}</span>
                    <Columns2 className="h-4 w-4" aria-hidden="true" />
                  </TabsTrigger>
                  <TabsTrigger
                    value="table"
                    id="table-view-tab"
                    className="w-15 px-3"
                  >
                    <span className="sr-only">
                      {t("studentList.tableView")}
                    </span>
                    <ListIcon className="h-4 w-4" aria-hidden="true" />
                  </TabsTrigger>
                </TabsList>
              )}

              <Button
                variant="outline"
                aria-label={t("studentList.actions.share")}
                onClick={onShareClick ? onShareClick : undefined}
                className="h-8 rounded-[6px] has-[>svg]:px-4 has-[>svg]:py-2"
              >
                <p className="text-xs font-normal">
                  {t("studentList.actions.share")}
                </p>
                <ShareIcon className="h-4 w-4" aria-hidden="true" />
              </Button>

              {headerActions.length > 0 && (
                <HeaderActions
                  actions={headerActions}
                  disabled={loading || totalCount === 0}
                  size="sm"
                />
              )}
            </div>
          </div>
        </CardHeader>

        {enableGridView && (
          <TabsContent className="mt-2" value="grid">
            <CardContent>
              <StudentGrid
                students={students}
                isLoading={loading || !org?.id}
                page={page}
                setPage={onPageChange}
                setPerPage={setPerPage}
                numRowsPerPage={NUMS_GRID_ROWS}
                totalPages={totalPages}
                isError={isError}
                hasActiveFilters={hasActiveFilters}
                profileSource={profileSource}
              />
            </CardContent>
          </TabsContent>
        )}

        <TabsContent className="mt-0" value="table">
          <div className="rounded-md px-0 pb-0 text-sm text-muted-foreground">
            <StudentsListSGrid
              students={students}
              showMessages={showMessages}
              page={page}
              perPage={perPage}
              totalPages={totalPages}
              onPageChange={onPageChange}
              isLoading={loading || !org?.id}
              hasNextPage={page < totalPages}
              hasPreviousPage={page > 1}
              ordering={ordering}
              setOrdering={setOrdering}
              isError={isError}
              hasActiveFilters={hasActiveFilters}
              profileSource={profileSource}
            />
          </div>
        </TabsContent>
      </Card>
    </Tabs>
  );
};
